import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ActionMenu } from '@milanote/roma/application';
import { Popover } from '@milanote/roma';
import { Button } from 'react-aria-components';

import type { MNHotInstance } from '../../bridge/tableHotInstanceTypes';
import { dangerouslyUseActiveTable } from '../../bridge/store/hotTableStoreHooks';
import type { ActiveTableObject } from '../../bridge/store/hotTableStoreTypes';
import TableContextMenuV2 from '../handlers/contextMenu/TableContextMenuV2';
import useTableContextMenuActions, {
    useTableContextMenuKeyboardHandling,
} from '../../bridge/handlers/contextMenu/useTableContextMenuActions';
import {
    tableDesktopToolbarMenuConfig,
    tableTabletToolbarMenuConfig,
} from '../handlers/contextMenu/tableContextMenuV2Config';
import Icon from '../../../../components/icons/Icon';
import { selectIsTabletFormFactor } from '../../../../platform/platformSelector';
import { userLanguagePreferenceSelector } from '../../../../user/currentUserSelector';
import useToolbarPopover from '../../../../workspace/toolbar/hooks/useToolbarPopover';
import ToolbarTool from '../../../../workspace/toolbar/components/ToolbarTool';

// TODO WEB-16910: Share More tool styling without relying on legacy popup class names.
import '../../../../workspace/toolbar/components/moreTool/MoreTool.scss';

type TableContextMenuToolProps = {
    name?: string;
};

/**
 * Opens the active table cell menu from the table toolbar.
 * TODO WEB-16910: Consolidate the shared More tool shell with DesktopElementContextMenuTool.
 */
const TableContextMenuTool: React.FC<TableContextMenuToolProps> = ({ name = 'More', ...toolProps }) => {
    const activeTable = dangerouslyUseActiveTable();
    const locale = useSelector(userLanguagePreferenceSelector);
    const isTablet = useSelector(selectIsTabletFormFactor);
    const { isOpen, onOpenChange, onTriggerPress, onTriggerPressStart } = useToolbarPopover();
    const [openTable, setOpenTable] = useState<ActiveTableObject | null>(null);
    const hotTableInstanceRef = useRef<MNHotInstance | null>(null);
    const tableForMenu = isOpen ? openTable : activeTable;
    hotTableInstanceRef.current = tableForMenu?.instance || null;
    const getHotInstance = useCallback(() => hotTableInstanceRef.current, []);
    const handleOpenChange = useCallback(
        (nextIsOpen: boolean) => {
            if (nextIsOpen) {
                if (!activeTable) return;
                setOpenTable(activeTable);
            } else {
                setOpenTable(null);
            }

            onOpenChange(nextIsOpen);
        },
        [activeTable, onOpenChange],
    );
    const { runGridAction, runClipboardAction } = useTableContextMenuActions({ getHotInstance });
    useTableContextMenuKeyboardHandling(getHotInstance, isOpen);

    const menuConfig = isTablet ? tableTabletToolbarMenuConfig : tableDesktopToolbarMenuConfig;

    return (
        <div className="MoreTool ToolbarPopupTool">
            <Popover.MenuTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
                <Button
                    className="Button"
                    aria-label={name}
                    onPress={onTriggerPress}
                    onPressStart={onTriggerPressStart}
                >
                    <ToolbarTool {...toolProps} isOpen={isOpen} tooltipEnabled={!isOpen}>
                        <Icon name="toolbar-more" />
                    </ToolbarTool>
                </Button>
                <Popover className={ActionMenu.styles.width} placement="end top" offset={4} crossOffset={6}>
                    {tableForMenu && (
                        <TableContextMenuV2
                            hotTableInstanceRef={hotTableInstanceRef}
                            locale={locale}
                            elementId={tableForMenu.elementId}
                            runGridAction={runGridAction}
                            runClipboardAction={runClipboardAction}
                            menuConfig={menuConfig}
                            size="sm"
                        />
                    )}
                </Popover>
            </Popover.MenuTrigger>
        </div>
    );
};

export default TableContextMenuTool;
